import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { Player, BallArsenalItem, FAQItem, VideoLink, CoachAssessment, JuniorGoldResult, JuniorGoldDivision, Accomplishment, AccomplishmentLevel } from '@/types';
import { ArrowLeft, Eye, Plus, Save, Search, Trash2 } from 'lucide-react';
import { BallPicker, type BallPickerSelection } from '@/components/BallPicker';
import { LeaguePanel } from '@/components/LeaguePanel';
import { LeagueHistoryPanel } from '@/components/LeagueHistoryPanel';

const BALL_ROLES: BallArsenalItem['role'][] = ['benchmark', 'heavy-oil', 'medium', 'burn', 'spare', 'situational'];
const VIDEO_TYPES: VideoLink['type'][] = ['match-play', 'practice', 'ball-motion', 'highlight'];
const JG_DIVISIONS: JuniorGoldDivision[] = ['U14B', 'U14G', 'U16B', 'U16G', 'U18B', 'U18G'];
const ACCOMPLISHMENT_LEVELS: AccomplishmentLevel[] = ['league', 'city', 'regional', 'state', 'national'];

const emptyBall = (pos: number): BallArsenalItem => ({
  name: '',
  brand: '',
  coverstock: '',
  core: '',
  surface: '',
  bag: 1,
  bagPosition: pos,
  role: 'benchmark',
  notes: '',
  imageUrl: '',
  ballDbId: '',
});

const emptyAssessment: CoachAssessment = {
  updatedAt: new Date(),
  physicalGame: {},
  mentalGame: {},
  summary: '',
  projection: '',
};

const inputCls =
  'w-full px-3 py-2 border border-tiger-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-tiger-primary-black focus:border-transparent transition text-sm';

const Section: React.FC<{ title: string; hint?: string; children: React.ReactNode }> = ({ title, hint, children }) => (
  <section className="bg-white rounded-2xl shadow-tiger p-6">
    <h2 className="font-heading text-2xl tracking-wide text-tiger-primary-black uppercase">{title}</h2>
    {hint && <p className="text-sm text-tiger-neutral-500 mt-1 mb-4">{hint}</p>}
    <div className={hint ? '' : 'mt-4'}>{children}</div>
  </section>
);

const Slider: React.FC<{ label: string; value?: number; onChange: (v: number | undefined) => void }> = ({
  label,
  value,
  onChange,
}) => (
  <div className="flex items-center gap-3">
    <span className="w-40 text-sm font-bold text-tiger-neutral-700 shrink-0">{label}</span>
    <input
      type="range"
      min={1}
      max={10}
      step={1}
      value={value ?? 5}
      onChange={(e) => onChange(parseInt(e.target.value))}
      className="flex-1 accent-black"
    />
    <span className="w-14 text-right font-display text-lg">{value !== undefined ? `${value}/10` : '—'}</span>
    <button
      type="button"
      onClick={() => onChange(undefined)}
      className="text-xs text-tiger-neutral-400 hover:text-tiger-neutral-700"
      title="Clear rating"
    >
      clear
    </button>
  </div>
);

export const CoachRecruitingEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [player, setPlayer] = useState<Player | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  // form state
  const [statsPlayerId, setStatsPlayerId] = useState('');
  const [hand, setHand] = useState<string>('');
  const [gpa, setGpa] = useState('');
  const [actSat, setActSat] = useState('');
  const [major, setMajor] = useState('');
  const [goals, setGoals] = useState('');
  const [arsenal, setArsenal] = useState<BallArsenalItem[]>([]);
  const [videos, setVideos] = useState<VideoLink[]>([]);
  const [faq, setFaq] = useState<FAQItem[]>([]);
  const [assessment, setAssessment] = useState<CoachAssessment>(emptyAssessment);
  const [juniorGold, setJuniorGold] = useState<JuniorGoldResult[]>([]);
  const [accomplishments, setAccomplishments] = useState<Accomplishment[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    (async () => {
      if (!id) return;
      const snap = await getDoc(doc(db, 'players', id));
      if (!snap.exists()) {
        setLoading(false);
        return;
      }
      const p = { id: snap.id, ...snap.data() } as Player;
      setPlayer(p);
      setStatsPlayerId(p.statsPlayerId || '');
      setHand(p.hand || '');
      setGpa(p.academics?.gpa !== undefined ? String(p.academics.gpa) : '');
      setActSat(p.academics?.actSat || '');
      setMajor(p.academics?.intendedMajor || '');
      setGoals(p.goals || '');
      setArsenal(p.ballArsenal || []);
      setVideos(p.videoLinks || []);
      setFaq(p.faq || []);
      setAssessment(p.assessment ? { ...emptyAssessment, ...p.assessment } : emptyAssessment);
      setJuniorGold(p.juniorGoldResults || []);
      setAccomplishments(p.accomplishments || []);
      setLoading(false);
    })();
  }, [id]);

  const setBall = (i: number, patch: Partial<BallArsenalItem>) =>
    setArsenal((prev) => prev.map((b, j) => (j === i ? { ...b, ...patch } : b)));
  const setVideo = (i: number, patch: Partial<VideoLink>) =>
    setVideos((prev) => prev.map((v, j) => (j === i ? { ...v, ...patch } : v)));
  const setFaqItem = (i: number, patch: Partial<FAQItem>) =>
    setFaq((prev) => prev.map((f, j) => (j === i ? { ...f, ...patch } : f)));
  const setJg = (i: number, patch: Partial<JuniorGoldResult>) =>
    setJuniorGold((prev) => prev.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const setAccomplishment = (i: number, patch: Partial<Accomplishment>) =>
    setAccomplishments((prev) => prev.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const setPhysical = (k: keyof NonNullable<CoachAssessment['physicalGame']>, v: number | undefined) =>
    setAssessment((a) => ({ ...a, physicalGame: { ...a.physicalGame, [k]: v } }));
  const setMental = (k: keyof NonNullable<CoachAssessment['mentalGame']>, v: number | undefined) =>
    setAssessment((a) => ({ ...a, mentalGame: { ...a.mentalGame, [k]: v } }));

  const handlePickBall = (ball: BallPickerSelection) =>
    setArsenal((prev) => [
      ...prev,
      {
        name: ball.name,
        brand: ball.brand,
        coverstock: ball.coverstock,
        core: ball.core,
        surface: '',
        bag: 1,
        bagPosition: prev.length + 1,
        role: 'benchmark',
        notes: '',
        imageUrl: ball.imageUrl,
        ballDbId: ball.ballDbId,
      },
    ]);

  const handleSave = async () => {
    if (!id) return;
    setSaving(true);
    setMessage('');
    try {
      const academics: Record<string, unknown> = {};
      if (gpa.trim() !== '' && !Number.isNaN(parseFloat(gpa))) academics.gpa = parseFloat(gpa);
      if (actSat.trim()) academics.actSat = actSat.trim();
      if (major.trim()) academics.intendedMajor = major.trim();

      const cleanAssessment = {
        ...assessment,
        updatedAt: new Date(),
        physicalGame: Object.fromEntries(Object.entries(assessment.physicalGame || {}).filter(([, v]) => v !== undefined)),
        mentalGame: Object.fromEntries(Object.entries(assessment.mentalGame || {}).filter(([, v]) => v !== undefined)),
      };
      const hasAssessment =
        cleanAssessment.summary.trim() !== '' ||
        Object.keys(cleanAssessment.physicalGame).length > 0 ||
        Object.keys(cleanAssessment.mentalGame).length > 0;

      await updateDoc(doc(db, 'players', id), {
        statsPlayerId: statsPlayerId.trim() || null,
        hand: hand || null,
        academics: Object.keys(academics).length ? academics : null,
        goals: goals.trim() || null,
        ballArsenal: arsenal.filter((b) => b.name.trim()),
        videoLinks: videos.filter((v) => v.url.trim()),
        faq: faq.filter((f) => f.question.trim() && f.answer.trim()),
        assessment: hasAssessment ? cleanAssessment : null,
        juniorGoldResults: juniorGold
          .filter((r) => r.year.trim() && r.division)
          .map((r) => ({ year: r.year.trim(), division: r.division, ...(r.place ? { place: r.place } : {}) })),
        accomplishments: accomplishments
          .filter((a) => a.title.trim())
          .map((a) => ({
            title: a.title.trim(),
            year: a.year.trim(),
            ...(a.placement?.trim() ? { placement: a.placement.trim() } : {}),
            ...(a.level ? { level: a.level } : {}),
            ...(a.notes?.trim() ? { notes: a.notes.trim() } : {}),
          })),
        updatedAt: serverTimestamp(),
      });
      setMessage('Saved.');
    } catch (e) {
      console.error('Save failed:', e);
      setMessage('Save failed. Check console.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-tiger-primary-black" />
      </div>
    );
  }

  if (!player) {
    return (
      <div className="min-h-[50vh] flex flex-col items-center justify-center gap-3">
        <p className="font-display text-2xl">Player not found</p>
        <Link to="/coach/dashboard" className="underline">Back to dashboard</Link>
      </div>
    );
  }

  return (
    <div className="bg-tiger-neutral-50 min-h-screen">
      <div className="max-w-4xl mx-auto px-4 py-10 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <Link to="/coach/dashboard" className="inline-flex items-center gap-1 text-sm text-tiger-neutral-600 hover:text-tiger-primary-black">
              <ArrowLeft className="w-4 h-4" /> Dashboard
            </Link>
            <h1 className="font-display text-3xl text-tiger-primary-black mt-1">
              Recruiting Profile: {player.name}
            </h1>
          </div>
          <div className="flex gap-2">
            <Link
              to={`/player/${player.id}`}
              className="inline-flex items-center gap-2 px-4 py-2 border-2 border-tiger-neutral-300 rounded-lg font-bold text-tiger-neutral-700 hover:bg-white transition-colors"
            >
              <Eye className="w-4 h-4" /> Preview
            </Link>
            <button
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-2 px-5 py-2 bg-tiger-primary-black text-white rounded-lg font-bold hover:bg-tiger-neutral-800 transition-colors disabled:opacity-50"
            >
              <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save All'}
            </button>
          </div>
        </div>
        {message && (
          <div className={`px-4 py-3 rounded-lg font-bold text-sm ${message === 'Saved.' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-700'}`}>
            {message}
          </div>
        )}

        <Section title="Basics" hint="Stats Player ID links this profile to the Stats Tracker for live analytics. Find it in the Stats app URL for the player.">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-bold text-tiger-neutral-700 block mb-1">Stats Player ID</label>
              <input className={inputCls} value={statsPlayerId} onChange={(e) => setStatsPlayerId(e.target.value)} placeholder="from bowlingstatstracker" />
            </div>
            <div>
              <label className="text-sm font-bold text-tiger-neutral-700 block mb-1">Hand / Style</label>
              <select className={inputCls} value={hand} onChange={(e) => setHand(e.target.value)}>
                <option value="">Not set</option>
                <option value="right">Right-handed</option>
                <option value="left">Left-handed</option>
                <option value="two-handed">Two-handed</option>
              </select>
            </div>
          </div>
        </Section>

        {player && <LeaguePanel player={player} />}

        {player && <LeagueHistoryPanel player={player} />}

        <Section title="Academics & Goals">
          <div className="grid sm:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="text-sm font-bold text-tiger-neutral-700 block mb-1">GPA</label>
              <input className={inputCls} value={gpa} onChange={(e) => setGpa(e.target.value)} placeholder="3.85" inputMode="decimal" />
            </div>
            <div>
              <label className="text-sm font-bold text-tiger-neutral-700 block mb-1">ACT / SAT</label>
              <input className={inputCls} value={actSat} onChange={(e) => setActSat(e.target.value)} placeholder="ACT 28" />
            </div>
            <div>
              <label className="text-sm font-bold text-tiger-neutral-700 block mb-1">Intended Major</label>
              <input className={inputCls} value={major} onChange={(e) => setMajor(e.target.value)} placeholder="Engineering" />
            </div>
          </div>
          <label className="text-sm font-bold text-tiger-neutral-700 block mb-1">Goals</label>
          <textarea className={inputCls} rows={3} value={goals} onChange={(e) => setGoals(e.target.value)} placeholder="College bowling goals, dream schools, what they're working toward…" />
        </Section>

        <Section title="Junior Gold" hint="Junior Gold Championships finishes, division + where they placed. A big recruiting signal.">
          <div className="space-y-3">
            {juniorGold.map((r, i) => (
              <div key={i} className="grid sm:grid-cols-[1fr_1fr_1fr_auto] gap-3 items-end">
                <div>
                  <label className="text-xs font-bold text-tiger-neutral-500 block mb-1">Year</label>
                  <input className={inputCls} value={r.year} onChange={(e) => setJg(i, { year: e.target.value })} placeholder="2025" inputMode="numeric" />
                </div>
                <div>
                  <label className="text-xs font-bold text-tiger-neutral-500 block mb-1">Division</label>
                  <select className={inputCls} value={r.division} onChange={(e) => setJg(i, { division: e.target.value as JuniorGoldDivision })}>
                    <option value="">Select</option>
                    {JG_DIVISIONS.map((d) => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold text-tiger-neutral-500 block mb-1">Placed</label>
                  <input
                    className={inputCls}
                    type="number"
                    min={1}
                    value={r.place ?? ''}
                    onChange={(e) => setJg(i, { place: e.target.value ? parseInt(e.target.value, 10) : undefined })}
                    placeholder="11"
                  />
                </div>
                <button type="button" onClick={() => setJuniorGold((prev) => prev.filter((_, j) => j !== i))} className="p-2 text-tiger-neutral-500 hover:text-red-600" title="Remove">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => setJuniorGold((prev) => [...prev, { year: '', division: '' as JuniorGoldDivision }])}
              className="inline-flex items-center gap-2 px-4 py-2 border-2 border-dashed border-tiger-neutral-300 rounded-lg font-bold text-tiger-neutral-600 hover:border-tiger-primary-black hover:text-tiger-primary-black transition-colors"
            >
              <Plus className="w-4 h-4" /> Add Junior Gold result
            </button>
          </div>
        </Section>

        <Section
          title="Accomplishments"
          hint="Tournament wins and placements at every level, league, city, regional, state, national. Shows recruiters (and families) these kids compete and win under pressure."
        >
          <div className="space-y-3">
            {accomplishments.map((a, i) => (
              <div key={i} className="space-y-2 pb-3 border-b border-tiger-neutral-200 last:border-0 last:pb-0">
                <div className="grid sm:grid-cols-[2fr_1fr_1fr_1fr_auto] gap-3 items-end">
                  <div>
                    <label className="text-xs font-bold text-tiger-neutral-500 block mb-1">Tournament / Event</label>
                    <input className={inputCls} value={a.title} onChange={(e) => setAccomplishment(i, { title: e.target.value })} placeholder="Missouri Pepsi State Championships" />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-tiger-neutral-500 block mb-1">Year</label>
                    <input className={inputCls} value={a.year} onChange={(e) => setAccomplishment(i, { year: e.target.value })} placeholder="2025" inputMode="numeric" />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-tiger-neutral-500 block mb-1">Placed</label>
                    <input className={inputCls} value={a.placement ?? ''} onChange={(e) => setAccomplishment(i, { placement: e.target.value })} placeholder="1st" />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-tiger-neutral-500 block mb-1">Level</label>
                    <select className={inputCls} value={a.level ?? ''} onChange={(e) => setAccomplishment(i, { level: (e.target.value || undefined) as Accomplishment['level'] })}>
                      <option value="">Select</option>
                      {ACCOMPLISHMENT_LEVELS.map((l) => (
                        <option key={l} value={l}>{l.charAt(0).toUpperCase() + l.slice(1)}</option>
                      ))}
                    </select>
                  </div>
                  <button type="button" onClick={() => setAccomplishments((prev) => prev.filter((_, j) => j !== i))} className="p-2 text-tiger-neutral-500 hover:text-red-600" title="Remove">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <input
                  className={inputCls}
                  value={a.notes ?? ''}
                  onChange={(e) => setAccomplishment(i, { notes: e.target.value })}
                  placeholder="Notes, e.g. “Shot 245 in the title match” (optional)"
                />
              </div>
            ))}
            <button
              type="button"
              onClick={() => setAccomplishments((prev) => [...prev, { title: '', year: '' }])}
              className="inline-flex items-center gap-2 px-4 py-2 border-2 border-dashed border-tiger-neutral-300 rounded-lg font-bold text-tiger-neutral-600 hover:border-tiger-primary-black hover:text-tiger-primary-black transition-colors"
            >
              <Plus className="w-4 h-4" /> Add accomplishment
            </button>
          </div>
        </Section>

        <Section title="The Bag" hint="Bag position 1 = benchmark ball, first out of the bag. Coverstock and core matter to recruiters and ball reps.">
          <div className="space-y-4">
            {arsenal.map((b, i) => (
              <div key={i} className="border border-tiger-neutral-200 rounded-xl p-4 flex gap-3 relative">
                {b.imageUrl && (
                  <img
                    src={b.imageUrl}
                    alt={`${b.brand} ${b.name}`.trim()}
                    className="w-12 h-12 object-contain bg-tiger-neutral-50 rounded shrink-0"
                  />
                )}
                <div className="flex-1 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div>
                  <label className="text-xs font-bold text-tiger-neutral-500 block mb-1">Ball name</label>
                  <input className={inputCls} value={b.name} onChange={(e) => setBall(i, { name: e.target.value })} placeholder="Phaze II" />
                </div>
                <div>
                  <label className="text-xs font-bold text-tiger-neutral-500 block mb-1">Brand</label>
                  <input className={inputCls} value={b.brand} onChange={(e) => setBall(i, { brand: e.target.value })} placeholder="Storm" />
                </div>
                <div>
                  <label className="text-xs font-bold text-tiger-neutral-500 block mb-1">Coverstock</label>
                  <input className={inputCls} value={b.coverstock} onChange={(e) => setBall(i, { coverstock: e.target.value })} placeholder="R2S Solid" />
                </div>
                <div>
                  <label className="text-xs font-bold text-tiger-neutral-500 block mb-1">Core</label>
                  <input className={inputCls} value={b.core || ''} onChange={(e) => setBall(i, { core: e.target.value })} placeholder="Velocity" />
                </div>
                <div>
                  <label className="text-xs font-bold text-tiger-neutral-500 block mb-1">Surface</label>
                  <input className={inputCls} value={b.surface || ''} onChange={(e) => setBall(i, { surface: e.target.value })} placeholder="3000 grit" />
                </div>
                <div>
                  <label className="text-xs font-bold text-tiger-neutral-500 block mb-1">Bag #</label>
                  <input
                    className={inputCls}
                    type="number"
                    min={1}
                    value={b.bag ?? 1}
                    onChange={(e) => setBall(i, { bag: parseInt(e.target.value) || 1 })}
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-tiger-neutral-500 block mb-1">Bag position</label>
                  <input
                    className={inputCls}
                    type="number"
                    min={1}
                    value={b.bagPosition}
                    onChange={(e) => setBall(i, { bagPosition: parseInt(e.target.value) || 1 })}
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-tiger-neutral-500 block mb-1">Role</label>
                  <select className={inputCls} value={b.role} onChange={(e) => setBall(i, { role: e.target.value as BallArsenalItem['role'] })}>
                    {BALL_ROLES.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold text-tiger-neutral-500 block mb-1">Notes</label>
                  <input className={inputCls} value={b.notes || ''} onChange={(e) => setBall(i, { notes: e.target.value })} placeholder="Fresh oil look" />
                </div>
                </div>
                <button
                  type="button"
                  onClick={() => setArsenal((prev) => prev.filter((_, j) => j !== i))}
                  className="absolute -top-2 -right-2 bg-white border border-tiger-neutral-300 rounded-full p-1.5 text-tiger-neutral-500 hover:text-red-600 hover:border-red-300 shadow-tiger"
                  title="Remove ball"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => setArsenal((prev) => [...prev, emptyBall(prev.length + 1)])}
                className="inline-flex items-center gap-2 px-4 py-2 border-2 border-dashed border-tiger-neutral-300 rounded-lg font-bold text-tiger-neutral-600 hover:border-tiger-primary-black hover:text-tiger-primary-black transition-colors"
              >
                <Plus className="w-4 h-4" /> Add ball
              </button>
              <button
                type="button"
                onClick={() => setPickerOpen(true)}
                className="inline-flex items-center gap-2 px-4 py-2 border-2 border-tiger-neutral-300 rounded-lg font-bold text-tiger-neutral-600 hover:border-tiger-primary-black hover:text-tiger-primary-black transition-colors"
              >
                <Search className="w-4 h-4" /> Search ball database
              </button>
            </div>
          </div>
        </Section>

        <Section title="Coach's Assessment" hint="1-10 scales. Shown publicly with your summary, so grade like a recruiter is reading it.">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-3">
              <h3 className="font-bold text-tiger-neutral-700">Physical game</h3>
              <Slider label="Balance" value={assessment.physicalGame?.balance} onChange={(v) => setPhysical('balance', v)} />
              <Slider label="Timing" value={assessment.physicalGame?.timing} onChange={(v) => setPhysical('timing', v)} />
              <Slider label="Release" value={assessment.physicalGame?.release} onChange={(v) => setPhysical('release', v)} />
              <Slider label="Footwork" value={assessment.physicalGame?.footwork} onChange={(v) => setPhysical('footwork', v)} />
              <Slider label="Arm swing" value={assessment.physicalGame?.armSwing} onChange={(v) => setPhysical('armSwing', v)} />
              <Slider label="Versatility" value={assessment.physicalGame?.versatility} onChange={(v) => setPhysical('versatility', v)} />
            </div>
            <div className="space-y-3">
              <h3 className="font-bold text-tiger-neutral-700">Mental game</h3>
              <Slider label="Composure" value={assessment.mentalGame?.composure} onChange={(v) => setMental('composure', v)} />
              <Slider label="Lane play & strategy" value={assessment.mentalGame?.strategy} onChange={(v) => setMental('strategy', v)} />
              <Slider label="Spare system" value={assessment.mentalGame?.sparesystem} onChange={(v) => setMental('sparesystem', v)} />
              <Slider label="Coachability" value={assessment.mentalGame?.coachability} onChange={(v) => setMental('coachability', v)} />
            </div>
          </div>
          <div className="mt-6 space-y-4">
            <div>
              <label className="text-sm font-bold text-tiger-neutral-700 block mb-1">Summary (public)</label>
              <textarea className={inputCls} rows={3} value={assessment.summary} onChange={(e) => setAssessment((a) => ({ ...a, summary: e.target.value }))} placeholder="Narrative evaluation a college coach would want to read…" />
            </div>
            <div>
              <label className="text-sm font-bold text-tiger-neutral-700 block mb-1">Projection</label>
              <input className={inputCls} value={assessment.projection || ''} onChange={(e) => setAssessment((a) => ({ ...a, projection: e.target.value }))} placeholder="e.g., NAIA-ready now, NCAA D1 upside" />
            </div>
          </div>
        </Section>

        <Section title="Film Room" hint="Unlisted YouTube links work best. Ball-motion breakdowns, match play, highlights.">
          <div className="space-y-3">
            {videos.map((v, i) => (
              <div key={i} className="grid sm:grid-cols-[1fr_2fr_auto_auto] gap-3 items-end">
                <div>
                  <label className="text-xs font-bold text-tiger-neutral-500 block mb-1">Title</label>
                  <input className={inputCls} value={v.title} onChange={(e) => setVideo(i, { title: e.target.value })} placeholder="League night, Jan 2026" />
                </div>
                <div>
                  <label className="text-xs font-bold text-tiger-neutral-500 block mb-1">URL</label>
                  <input className={inputCls} value={v.url} onChange={(e) => setVideo(i, { url: e.target.value })} placeholder="https://youtu.be/…" />
                </div>
                <div>
                  <label className="text-xs font-bold text-tiger-neutral-500 block mb-1">Type</label>
                  <select className={inputCls} value={v.type} onChange={(e) => setVideo(i, { type: e.target.value as VideoLink['type'] })}>
                    {VIDEO_TYPES.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <button
                  type="button"
                  onClick={() => setVideos((prev) => prev.filter((_, j) => j !== i))}
                  className="p-2 text-tiger-neutral-500 hover:text-red-600"
                  title="Remove video"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => setVideos((prev) => [...prev, { title: '', url: '', type: 'match-play' }])}
              className="inline-flex items-center gap-2 px-4 py-2 border-2 border-dashed border-tiger-neutral-300 rounded-lg font-bold text-tiger-neutral-600 hover:border-tiger-primary-black hover:text-tiger-primary-black transition-colors"
            >
              <Plus className="w-4 h-4" /> Add video
            </button>
          </div>
        </Section>

        <Section title="Get To Know: Q&A" hint="Interview-style questions. Players can also answer these from their own dashboard.">
          <div className="space-y-3">
            {faq.map((f, i) => (
              <div key={i} className="grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
                <div>
                  <label className="text-xs font-bold text-tiger-neutral-500 block mb-1">Question</label>
                  <input className={inputCls} value={f.question} onChange={(e) => setFaqItem(i, { question: e.target.value })} placeholder="What's your go-to shot under pressure?" />
                </div>
                <div>
                  <label className="text-xs font-bold text-tiger-neutral-500 block mb-1">Answer</label>
                  <input className={inputCls} value={f.answer} onChange={(e) => setFaqItem(i, { answer: e.target.value })} />
                </div>
                <button
                  type="button"
                  onClick={() => setFaq((prev) => prev.filter((_, j) => j !== i))}
                  className="p-2 text-tiger-neutral-500 hover:text-red-600"
                  title="Remove question"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => setFaq((prev) => [...prev, { question: '', answer: '' }])}
              className="inline-flex items-center gap-2 px-4 py-2 border-2 border-dashed border-tiger-neutral-300 rounded-lg font-bold text-tiger-neutral-600 hover:border-tiger-primary-black hover:text-tiger-primary-black transition-colors"
            >
              <Plus className="w-4 h-4" /> Add question
            </button>
          </div>
        </Section>

        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 px-8 py-3 bg-tiger-primary-black text-white rounded-lg font-bold hover:bg-tiger-neutral-800 transition-colors disabled:opacity-50"
          >
            <Save className="w-5 h-5" /> {saving ? 'Saving…' : 'Save All'}
          </button>
        </div>
      </div>

      <BallPicker open={pickerOpen} onClose={() => setPickerOpen(false)} onSelect={handlePickBall} />
    </div>
  );
};
